import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getOrderDetail,
  editOrder,
  approveOrder,
  unapproveOrder,
  signOrder,
  cancelOrder,
  parsePlanQuantity,
  type OrderFlow,
  type OrderRecord,
} from '@/api/order';
import { isOutsideFlag, resolveSpecImage } from '@/api/product';
import { applyBack } from '@/api/back';
import { getUser } from '@/utils/auth';
import { useCart } from '@/hooks/useCart';
import OrderItemsTable, {
  ensureSingleTrailingEmpty,
  isEmptyOrderItem,
  type OrderItem,
  type ProductPickPayload,
} from './components/OrderItemsTable';
import OrderMetaEditFields, {
  type OrderMetaEditValue,
} from './components/OrderMetaEditFields';
import ProductSelector, { type SelectableProduct } from './components/ProductSelector';
import OrderProgressPanel from './components/OrderProgressPanel';
import ApproveModal from '@/pages/orders/components/ApproveModal';
import AfterSaleModal from '@/pages/orders/components/AfterSaleModal';
import CancelOrderModal from '@/pages/orders/components/CancelOrderModal';
import ConfirmModal from '@/pages/orders/components/ConfirmModal';
import SignReceiptModal from '@/pages/orders/components/SignReceiptModal';
import SignVoucherModal from '@/pages/orders/components/SignVoucherModal';
import { clearDynamicBreadcrumbTitle, setDynamicBreadcrumbTitle } from '@/hooks/useDynamicBreadcrumb';

import type { ApprovalStep } from '@/types/order';

interface DetailOrderItem extends OrderItem {
  goodsId: string;
  isOutside?: boolean;
}

interface OrderDetailView {
  rawId: string;
  orderSn: string;
  /** 下单人账号 ID */
  accountId: string;
  orderPerson: string;
  department: string;
  departmentId: string;
  totalAmount: number;
  status: string;
  stateLabel: string;
  displayState: string;
  isBackorder: boolean;
  remark: string;
  createdAt: string;
  approvedAt?: string;
  /** 单据打印日期（is_print 为 true 且存在 print_time） */
  printedAt?: string;
  shippedAt?: string;
  /** 签收时间 receive_time */
  receivedAt?: string;
  completedAt?: string;
  /** 取消时间（已取消时取 updated_at） */
  cancelledAt?: string;
  rejectedAt?: string;
  rejectReason?: string;
  items: DetailOrderItem[];
  approvalSteps: ApprovalStep[];
  canApprove: boolean;
  canEdit: boolean;
  canReverse: boolean;
  /** 当前用户是否为下单人（签收/确认收货等） */
  isOrderPerson: boolean;
  /** 签收凭证图片 */
  signImages: { id: string; uri: string }[];
  /** 科室是否开启采购计划控制（明细展示「计划已使用」） */
  isPurchasePlanEnabled: boolean;
  /** 企业售后期限天数（接口实时值） */
  afterSaleDays: number;
  /** 是否仍在售后申请期限内（后端计算） */
  canApplyAfterSale: boolean;
}

const STATE_TO_STATUS: Record<string, string> = {
  待审批: 'pending',
  待确认: 'pending',
  审批中: 'approving',
  已审批: 'approved',
  待发货: 'approved',
  出库中: 'shipped',
  已签收: 'received',
  已完成: 'completed',
  已拒绝: 'rejected',
  已取消: 'cancelled',
  退货中: 'shipped',
};

function getOrderStateTag(state: string, isBackorder = false): { color: string; label: string } {
  if (state === '已拒绝') return { color: '#ed4014', label: state };
  if (state === '已取消') return { color: '#c5c8ce', label: state };
  if (state === '已审批') return { color: '#2d8cf0', label: state };
  if (state === '出库中') return { color: '#f90', label: state };
  if (state === '已完成' && !isBackorder) return { color: '#19be6b', label: state };
  if (state === '退货中' && isBackorder) return { color: '#f90', label: state };
  if (state === '已完成' && isBackorder) return { color: '#28CA9D', label: '退货完成' };
  return { color: '#33bcb7', label: state || '未知' };
}

function mapApproveState(state: number | string | undefined): ApprovalStep['status'] {
  const n = Number(state);
  if (n === 1) return 'approved';
  if (n === 2) return 'rejected';
  return 'pending';
}

function mapFlowsToSteps(flows: OrderFlow[] | undefined): ApprovalStep[] {
  if (!Array.isArray(flows) || flows.length === 0) return [];
  return flows
    .filter((flow) => {
      const sort = Number(flow.sort ?? 0);
      const nowSort = Number(flow.now_step_sort ?? 0);
      const isOver = Number(flow.is_over ?? 0);
      return sort <= nowSort || isOver !== 1;
    })
    .map((flow, index) => {
      const status = mapApproveState(flow.approve_state);
      return {
        level: Number(flow.sort ?? index + 1) || index + 1,
        approverId: String(flow.account_id || ''),
        approverName:
          status === 'pending'
            ? String(flow.account_names || flow.account_name || '待定')
            : String(flow.account_name || flow.account_names || ''),
        role: String(flow.step_name || flow.step || `第${index + 1}级审批`),
        status,
        approvedAt: flow.approve_time ? String(flow.approve_time) : undefined,
        comment: flow.approve ? String(flow.approve) : undefined,
      };
    });
}

function accountIdsInclude(accountIds: string | null | undefined, userId: string | number | null | undefined) {
  if (userId == null || userId === '') return false;
  const uid = String(userId).trim();
  if (!uid) return false;
  return String(accountIds || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .some((id) => id === uid);
}

function getCurrentFlow(flows: OrderFlow[] | undefined): OrderFlow | null {
  if (!Array.isArray(flows) || flows.length === 0) return null;
  return flows.find((f) => Number(f.sort) === Number(f.now_step_sort)) || null;
}

function resolveCanApprove(raw: OrderRecord, userId: string | number | null | undefined) {
  const state = String(raw.state || '');
  if (state !== '待审批' && state !== '审批中') return false;
  const current = getCurrentFlow(raw.flows);
  if (!current || Number(current.approve_state) !== 0) return false;
  return accountIdsInclude(current.account_ids, userId);
}

function resolveCanEdit(raw: OrderRecord, userId: string | number | null | undefined) {
  const user = getUser();
  const uid = userId ?? user?.id;

  // 1. 账号具备修改权限
  const canEditAccount =
    user?.is_canedit === true || user?.is_canedit === 1 || user?.is_canedit === '1';
  if (!canEditAccount) return false;

  // 2. 订单处于可编辑状态（审批未结束，出库前）
  const state = String(raw.state || '');
  if (state !== '待审批' && state !== '审批中') return false;

  // 3. 当前账号属于当前审批节点（sort === now_step_sort 且未审）
  const current = getCurrentFlow(raw.flows);
  if (!current || Number(current.approve_state) !== 0) return false;
  return accountIdsInclude(current.account_ids, uid);
}

function resolveCanReverse(raw: OrderRecord, userId: string | number | null | undefined) {
  if (String(raw.state || '') !== '已审批') return false;
  const flows = Array.isArray(raw.flows) ? raw.flows : [];
  const lastPassed = [...flows]
    .filter((f) => Number(f.approve_state) === 1)
    .sort((a, b) => Number(b.sort ?? 0) - Number(a.sort ?? 0))[0];
  if (!lastPassed) return false;
  return accountIdsInclude(lastPassed.account_ids, userId);
}

function isOrderOwner(accountId: string | number | null | undefined, userId?: string | number | null) {
  const uid = userId ?? getUser()?.id;
  if (uid == null || uid === '' || accountId == null || accountId === '') return false;
  return String(uid) === String(accountId);
}

function getEnterpriseReturnDays(afterSaleDays?: number | string | null, user = getUser()) {
  const fromApi = Number(afterSaleDays);
  if (Number.isFinite(fromApi) && fromApi > 0) return fromApi;
  const raw = Number(user?.enterprise?.latest);
  if (Number.isFinite(raw) && raw > 0) return raw;
  return 0;
}

function isTruthyFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

/** 采购信息只展示日期，形如 2026-10-23 */
function formatOrderDate(value?: string | null) {
  const raw = String(value || '').trim();
  const matched = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (matched) return `${matched[1]}-${matched[2]}-${matched[3]}`;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw || '-';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function isSameDepartment(departmentId: string | number | null | undefined, user = getUser()) {
  const userDeptId = user?.department_id ?? user?.department?.id;
  if (userDeptId == null || userDeptId === '' || departmentId == null || departmentId === '') {
    return false;
  }
  return String(userDeptId) === String(departmentId);
}

function mapOrderDetail(
  raw: OrderRecord,
  afterSaleDaysFromRes?: number | string | null,
): OrderDetailView {
  const state = String(raw.state || '');
  const status = STATE_TO_STATUS[state] || 'pending';
  const isBackorder = Number(raw.is_backorder) === 1;
  const stateTag = getOrderStateTag(state, isBackorder);
  const amount = Number(raw.total ?? raw.amount ?? 0);
  const user = getUser();
  const planEnabled =
    raw.is_purchase_plan_enabled === true ||
    raw.is_purchase_plan_enabled === 1 ||
    raw.is_purchase_plan_enabled === '1';
  const afterSaleDays = getEnterpriseReturnDays(
    raw.after_sale_days ?? afterSaleDaysFromRes,
  );
  const items: DetailOrderItem[] = (raw.lists || []).map((row) => {
    const plan = row.purchase_plan;
    const hasPlan = planEnabled && plan != null;
    return {
      productId: String(row.goods_id || row.spec_id || row.id || ''),
      goodsId: String(row.goods_id || ''),
      name: row.goods_name || '未命名物资',
      quantity: Number(row.quantity ?? 0) || 0,
      unitPrice: Number(row.price ?? 0) || 0,
      unit: row.unit || '件',
      specId: row.spec_id != null ? String(row.spec_id) : undefined,
      spec: row.sku_name || '',
      image: resolveSpecImage(row.spec?.pic, row.goods?.m_pic),
      itemRemark: row.remark || '',
      isOutside: isOutsideFlag(row.isoutside ?? row.is_outside),
      planUsedQuantity: hasPlan ? parsePlanQuantity(plan?.used_quantity) : null,
      planQuantity: hasPlan ? parsePlanQuantity(plan?.plan_quantity) : null,
      planBaseUsedQuantity: hasPlan ? parsePlanQuantity(plan?.base_used_quantity) : null,
      planBaseQuantity: hasPlan ? parsePlanQuantity(plan?.base_plan_quantity) : null,
      planMultipleUnit: hasPlan ? parsePlanQuantity(plan?.multiple_unit ?? 1) : null,
    };
  });

  const flows = raw.flows || [];
  const rejectedFlow = flows.find((f) => Number(f.approve_state) === 2);
  const canApprove = resolveCanApprove(raw, user?.id);
  const canEdit = resolveCanEdit(raw, user?.id);
  const canReverse = resolveCanReverse(raw, user?.id);

  return {
    rawId: String(raw.id),
    orderSn: String(raw.order_sn || raw.id),
    accountId: String(raw.account_id || ''),
    orderPerson: raw.account_name || '',
    department: raw.department?.title || raw.office?.title || '',
    departmentId: String(raw.department_id || raw.department?.id || raw.office?.id || ''),
    totalAmount: Number.isFinite(amount) ? amount : 0,
    status,
    stateLabel: state,
    displayState: stateTag.label,
    isBackorder,
    remark: raw.remark || '',
    createdAt: String(raw.created_at || ''),
    approvedAt: raw.approve_time ? String(raw.approve_time) : undefined,
    printedAt:
      isTruthyFlag(raw.is_print) && raw.print_time
        ? String(raw.print_time)
        : undefined,
    shippedAt: raw.delivery_time ? String(raw.delivery_time) : undefined,
    receivedAt: raw.receive_time ? String(raw.receive_time) : undefined,
    completedAt: raw.complete_time ? String(raw.complete_time) : undefined,
    cancelledAt:
      state === '已取消' && raw.updated_at ? String(raw.updated_at) : undefined,
    rejectedAt: rejectedFlow?.approve_time ? String(rejectedFlow.approve_time) : undefined,
    rejectReason: rejectedFlow?.approve
      ? String(rejectedFlow.approve)
      : raw.reject_reason
        ? String(raw.reject_reason)
        : undefined,
    items,
    approvalSteps: mapFlowsToSteps(flows),
    canApprove,
    canEdit,
    canReverse,
    isOrderPerson: isOrderOwner(raw.account_id, user?.id),
    signImages: (raw.signimgs || [])
      .map((img, index) => ({
        id: String(img.id ?? `sign-${index}`),
        uri: String(img.uri || '').trim(),
      }))
      .filter((img) => !!img.uri),
    isPurchasePlanEnabled: planEnabled,
    afterSaleDays,
    canApplyAfterSale: isTruthyFlag(raw.can_apply_after_sale),
  };
}

type ActionModal =
  | { type: 'approve' }
  | { type: 'confirm-cancel' }
  | { type: 'confirm-reverse' }
  | { type: 'confirm-rebuy' }
  | { type: 'sign'; mode: 'sign' | 'proxy' }
  | { type: 'aftersale' }
  | { type: 'sign-voucher' }
  | null;

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { addItem } = useCart();

  const [order, setOrder] = useState<OrderDetailView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [editableItems, setEditableItems] = useState<OrderItem[]>([]);
  const [editableRemark, setEditableRemark] = useState('');
  const [editableMeta, setEditableMeta] = useState<OrderMetaEditValue>({
    departmentId: '',
    departmentTitle: '',
    accountId: '',
    accountName: '',
    createdAt: '',
  });
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [modal, setModal] = useState<ActionModal>(null);
  const [lastItemError, setLastItemError] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [pendingEdit, setPendingEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [reverseSubmitting, setReverseSubmitting] = useState(false);
  const [signSubmitting, setSignSubmitting] = useState(false);
  const [afterSaleSubmitting, setAfterSaleSubmitting] = useState(false);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const fetchDetail = useCallback(async () => {
    if (!id) {
      setLoadError('缺少订单参数');
      setOrder(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const res = await getOrderDetail(id);
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok || !res.data) {
        setLoadError(res.message || res.msg || '订单加载失败');
        setOrder(null);
        return;
      }
      const mapped = mapOrderDetail(res.data, res.after_sale_days);
      setOrder(mapped);
      setEditableItems(mapped.items.map((item) => ({ ...item })));
    } catch {
      setLoadError('订单加载失败，请稍后重试');
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    if (order?.orderSn) {
      setDynamicBreadcrumbTitle(`采购单 ${order.orderSn}`);
    } else {
      setDynamicBreadcrumbTitle('采购单');
    }
    return () => {
      clearDynamicBreadcrumbTitle();
    };
  }, [order?.orderSn]);

  useEffect(() => {
    if (searchParams.get('edit') === 'true') {
      setPendingEdit(true);
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!pendingEdit || !order || loading) return;
    setPendingEdit(false);
    if (order.canEdit) {
      setEditableItems(ensureSingleTrailingEmpty(order.items.map((item) => ({ ...item }))));
      setEditableRemark(order.remark || '');
      setEditableMeta({
        departmentId: order.departmentId || '',
        departmentTitle: order.department || '',
        accountId: order.accountId || '',
        accountName: order.orderPerson || '',
        createdAt: order.createdAt || '',
      });
      setEditMode(true);
    }
  }, [pendingEdit, order, loading]);

  const handleUpdateQuantity = (index: number, quantity: number) => {
    setEditableItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], quantity };
      return next;
    });
  };

  const handleUpdateRemark = (index: number, remark: string) => {
    setEditableItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], itemRemark: remark };
      return next;
    });
  };

  const handleUpdateSpec = (
    index: number,
    specId: string,
    price: number,
    model: string,
    unit?: string,
  ) => {
    setEditableItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        specId,
        unitPrice: price,
        spec: model,
        ...(unit ? { unit } : {}),
        isOutside: next[index].specOptions?.find((s) => s.id === specId)?.isOutside === true,
        // 规格变更后计划匹配失效，只读列显示「-」
        planUsedQuantity: null,
        planQuantity: null,
      };
      return next;
    });
    showToast(`规格已更新为「${model}」`);
  };

  const handleUpdateProduct = (index: number, product: ProductPickPayload) => {
    setEditableItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        productId: product.productId,
        name: product.name,
        image: product.image || '',
        specId: product.specId,
        spec: product.spec,
        unitPrice: product.unitPrice,
        unit: product.unit,
        quantity: next[index].quantity > 0 ? next[index].quantity : 1,
        specOptions: product.specs || [],
        planUsedQuantity: null,
        planQuantity: null,
        isOutside: product.isOutside === true,
      };
      // 最后一行被填满时补空行；并保证末尾有且仅有一行空行
      return ensureSingleTrailingEmpty(next);
    });
    showToast(`已更换为「${product.name}」并联动规格`);
  };

  const handleDeleteItem = (index: number) => {
    const target = editableItems[index];
    const filledCount = editableItems.filter((item) => !isEmptyOrderItem(item)).length;
    // 空行可随意删；非空行至少保留 1 条有效物资
    if (target && !isEmptyOrderItem(target) && filledCount <= 1) {
      setLastItemError(true);
      setTimeout(() => setLastItemError(false), 3000);
      return;
    }
    setEditableItems((prev) => {
      const next = prev.filter((_, i) => i !== index);
      // 删完后若最后一行已是有效物资，补一行空行；多余空行压成一行
      return ensureSingleTrailingEmpty(next);
    });
  };

  const handleAddProduct = (product: SelectableProduct) => {
    const newItem: OrderItem = {
      productId: product.goodsId,
      name: product.name,
      quantity: 1,
      unitPrice: product.price,
      unit: product.unit,
      specId: product.id,
      spec: product.model || '',
      image: product.image || '',
      isOutside: product.isOutside === true,
      specOptions: [
        {
          id: product.id,
          name: product.model || '',
          price: product.price,
          unit: product.unit,
          isOutside: product.isOutside === true,
        },
      ],
    };

    setEditableItems((prev) => {
      const emptyIndex = prev.findIndex((item) => isEmptyOrderItem(item));
      const next =
        emptyIndex >= 0
          ? (() => {
              const copy = [...prev];
              copy[emptyIndex] = newItem;
              return copy;
            })()
          : [...prev, newItem];
      return ensureSingleTrailingEmpty(next);
    });
    showToast(`已添加 "${product.name}"`);
  };

  const handleEnterEdit = () => {
    if (!order) return;
    // 进入编辑：现有物资 + 末尾一行空行（填满后再自动追加）
    setEditableItems(ensureSingleTrailingEmpty(order.items.map((item) => ({ ...item }))));
    setEditableRemark(order.remark || '');
    setEditableMeta({
      departmentId: order.departmentId || '',
      departmentTitle: order.department || '',
      accountId: order.accountId || '',
      accountName: order.orderPerson || '',
      createdAt: order.createdAt || '',
    });
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    if (!order || saving) return;
    const filledItems = editableItems.filter((item) => !isEmptyOrderItem(item));
    if (filledItems.length === 0) {
      setLastItemError(true);
      setTimeout(() => setLastItemError(false), 3000);
      showToast('请至少保留一件物资', 'error');
      return;
    }
    const incomplete = filledItems.find((item) => !item.productId || !item.specId);
    if (incomplete) {
      showToast('请完善物资与规格后再保存', 'error');
      return;
    }
    if (!editableMeta.departmentId) {
      showToast('请选择科室', 'error');
      return;
    }
    if (!editableMeta.accountId) {
      showToast('请选择下单人', 'error');
      return;
    }
    if (!editableMeta.createdAt) {
      showToast('请选择创建日期', 'error');
      return;
    }

    setSaving(true);
    try {
      const res = await editOrder({
        order_id: Number(order.rawId) || order.rawId,
        remark: editableRemark.trim(),
        department_id: Number(editableMeta.departmentId) || editableMeta.departmentId,
        account_id: Number(editableMeta.accountId) || editableMeta.accountId,
        created_at: editableMeta.createdAt,
        lists: filledItems.map((item) => ({
          goods_id: Number(item.productId) || item.productId,
          spec_id: Number(item.specId) || item.specId!,
          quantity: item.quantity,
          remark: item.itemRemark?.trim() || '',
        })),
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '订单修改失败', 'error');
        return;
      }

      // 重新拉取详情，保证图片等关联数据完整
      const detailRes = await getOrderDetail(order.rawId);
      const detailOk = detailRes.error === false || Number(detailRes.error) === 0;
      if (detailOk && detailRes.data) {
        const mapped = mapOrderDetail(detailRes.data, detailRes.after_sale_days);
        setOrder(mapped);
        setEditableItems(mapped.items.map((item) => ({ ...item })));
        setEditableRemark(mapped.remark || '');
        setEditableMeta({
          departmentId: mapped.departmentId || '',
          departmentTitle: mapped.department || '',
          accountId: mapped.accountId || '',
          accountName: mapped.orderPerson || '',
          createdAt: mapped.createdAt || '',
        });
      } else if (res.data) {
        const mapped = mapOrderDetail(res.data);
        setOrder(mapped);
        setEditableItems(mapped.items.map((item) => ({ ...item })));
        setEditableRemark(mapped.remark || '');
        setEditableMeta({
          departmentId: mapped.departmentId || '',
          departmentTitle: mapped.department || '',
          accountId: mapped.accountId || '',
          accountName: mapped.orderPerson || '',
          createdAt: mapped.createdAt || '',
        });
      } else {
        const totalAmount = filledItems.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
        setOrder((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            items: filledItems.map((item) => ({
              ...item,
              goodsId:
                'goodsId' in item && typeof (item as DetailOrderItem).goodsId === 'string'
                  ? (item as DetailOrderItem).goodsId
                  : item.productId,
            })),
            totalAmount,
            remark: editableRemark.trim(),
            departmentId: editableMeta.departmentId,
            department: editableMeta.departmentTitle,
            accountId: editableMeta.accountId,
            orderPerson: editableMeta.accountName,
            createdAt: editableMeta.createdAt,
          };
        });
        setEditableItems(filledItems);
      }
      setEditMode(false);
      showToast(res.message || '订单修改成功');
    } catch {
      showToast('订单修改失败，请稍后重试', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (!order) return;
    setEditableItems(order.items.map((item) => ({ ...item })));
    setEditableRemark(order.remark || '');
    setEditableMeta({
      departmentId: order.departmentId || '',
      departmentTitle: order.department || '',
      accountId: order.accountId || '',
      accountName: order.orderPerson || '',
      createdAt: order.createdAt || '',
    });
    setEditMode(false);
    setLastItemError(false);
  };

  const getQuickActions = (current: OrderDetailView) => {
    const actions: {
      key: string;
      label: string;
      icon: string;
      danger?: boolean;
      primary?: boolean;
      secondary?: boolean;
    }[] = [];

    if (current.canApprove) {
      actions.push({ key: 'approve', label: '审批', icon: 'ri-shield-check-line', primary: true });
    }
    if (current.canEdit) {
      actions.push({ key: 'edit', label: '修改', icon: 'ri-edit-line' });
    }

    const user = getUser();
    const isOwner = isOrderOwner(current.accountId, user?.id);
    const sameDepartment = isSameDepartment(current.departmentId, user);

    if (current.status === 'approving' && isOwner) {
      actions.push({ key: 'cancel', label: '取消', icon: 'ri-close-circle-line', danger: true });
    }
    if (current.canReverse) {
      actions.push({ key: 'reverse', label: '反审', icon: 'ri-arrow-go-back-line' });
    }
    if (current.stateLabel === '出库中') {
      if (isOwner) {
        actions.push({ key: 'sign', label: '签收', icon: 'ri-check-double-line', primary: true });
      } else if (sameDepartment) {
        actions.push({ key: 'proxy-sign', label: '代收', icon: 'ri-user-received-line', secondary: true });
      }
    }
    if (current.stateLabel === '已签收' || current.stateLabel === '已完成') {
      actions.push({ key: 'sign-voucher', label: '签收凭证', icon: 'ri-image-line' });
    }
    // 售后：已完成 + 退货期限内（后端 can_apply_after_sale）+ 当前账号与订单同科室
    if (current.status === 'completed' && sameDepartment && current.canApplyAfterSale) {
      actions.push({ key: 'aftersale', label: '售后', icon: 'ri-service-line' });
    }
    if (isOwner && (current.status === 'completed' || current.status === 'cancelled' || current.status === 'rejected')) {
      actions.push({ key: 'rebuy', label: '再购', icon: 'ri-shopping-cart-line' });
    }
    return actions;
  };

  const handleAction = (actionKey: string) => {
    if (!order || editMode) return;
    switch (actionKey) {
      case 'approve':
        setModal({ type: 'approve' });
        break;
      case 'edit':
        if (order.canEdit) handleEnterEdit();
        break;
      case 'cancel':
        setModal({ type: 'confirm-cancel' });
        break;
      case 'reverse':
        if (order.canReverse) setModal({ type: 'confirm-reverse' });
        break;
      case 'sign':
        setModal({ type: 'sign', mode: 'sign' });
        break;
      case 'proxy-sign':
        setModal({ type: 'sign', mode: 'proxy' });
        break;
      case 'sign-voucher':
        setModal({ type: 'sign-voucher' });
        break;
      case 'aftersale':
        if (!isSameDepartment(order.departmentId)) {
          showToast('订单不属于当前科室，无法申请售后', 'error');
          break;
        }
        if (!order.canApplyAfterSale) {
          const days = order.afterSaleDays;
          showToast(
            days <= 0
              ? '当前企业未开放售后申请'
              : `订单完成已超过 ${days} 天，无法申请售后`,
            'error',
          );
          break;
        }
        setModal({ type: 'aftersale' });
        break;
      case 'rebuy':
        setModal({ type: 'confirm-rebuy' });
        break;
      default:
        break;
    }
  };

  const handleApproveSubmit = async (isAgree: boolean, remark: string) => {
    if (!order || !modal || modal.type !== 'approve' || approveSubmitting) return;
    setApproveSubmitting(true);
    try {
      const res = await approveOrder({
        order_id: Number(order.rawId) || order.rawId,
        is_agree: isAgree,
        remark,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '审批失败', 'error');
        return;
      }
      showToast(isAgree ? '已同意审批' : '已拒绝审批');
      setModal(null);
      void fetchDetail();
    } catch {
      showToast('审批失败，请稍后重试', 'error');
    } finally {
      setApproveSubmitting(false);
    }
  };

  const handleCancelConfirm = async (reason: string) => {
    if (!order || !modal || modal.type !== 'confirm-cancel' || cancelSubmitting) return;
    setCancelSubmitting(true);
    try {
      const res = await cancelOrder({
        order_id: Number(order.rawId) || order.rawId,
        remark: reason,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '取消订单失败', 'error');
        return;
      }
      showToast(res.message || `订单 ${order.orderSn} 已取消`);
      setModal(null);
      void fetchDetail();
    } catch {
      showToast('取消订单失败，请稍后重试', 'error');
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleReverseConfirm = async () => {
    if (!order || !modal || modal.type !== 'confirm-reverse' || reverseSubmitting) return;
    setReverseSubmitting(true);
    try {
      const res = await unapproveOrder({
        order_id: Number(order.rawId) || order.rawId,
        remark: '反审退回',
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '反审失败', 'error');
        return;
      }
      showToast(res.message || '订单反审成功');
      setModal(null);
      void fetchDetail();
    } catch {
      showToast('反审失败，请稍后重试', 'error');
    } finally {
      setReverseSubmitting(false);
    }
  };

  const handleSignConfirm = async (imageUrls: string[]) => {
    if (!order || !modal || modal.type !== 'sign') return;
    setSignSubmitting(true);
    try {
      const res = await signOrder({
        order_id: Number(order.rawId) || order.rawId,
        images: imageUrls,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '签收失败', 'error');
        return;
      }
      showToast(
        modal.mode === 'proxy'
          ? `订单 ${order.orderSn} 代收成功`
          : `订单 ${order.orderSn} 签收成功`,
      );
      setModal(null);
      void fetchDetail();
    } catch {
      showToast('签收失败，请稍后重试', 'error');
    } finally {
      setSignSubmitting(false);
    }
  };

  const handleRebuyConfirm = () => {
    if (!order) return;
    order.items.forEach((item) => {
      const qty = Math.max(1, Math.floor(Number(item.quantity) || 1));
      const detail = item as DetailOrderItem;
      addItem({
        productId: detail.goodsId || item.productId,
        name: item.name,
        price: item.unitPrice,
        unit: item.unit,
        image: item.image || '',
        stock: -1,
        model: item.spec || item.unit,
        specId: item.specId || undefined,
        specName: item.spec || undefined,
        quantity: qty,
        isOutside: detail.isOutside === true,
      });
    });
    setModal(null);
    showToast('物资已加入购物车');
    setTimeout(() => navigate('/cart'), 600);
  };

  const handleAfterSaleSubmit = async (data: {
    orderId: string;
    products: Array<{
      productId: string;
      goodsId: string;
      specId: string;
      productName: string;
      quantity: number;
      unit: string;
      unitPrice: number;
    }>;
    type: '退货' | '换货';
    reason: string;
    description: string;
    images: string[];
  }) => {
    if (!order || !modal || modal.type !== 'aftersale') return;

    const validProducts = data.products.filter(
      (p) => Number(p.quantity) >= 1 && Number(p.goodsId) > 0 && Number(p.specId) > 0,
    );
    if (validProducts.length === 0) {
      showToast('请至少选择一件物资并填写售后数量', 'error');
      return;
    }

    setAfterSaleSubmitting(true);
    try {
      const res = await applyBack({
        order_id: Number(order.rawId) || order.rawId,
        type: data.type,
        specs: validProducts.map((p) => ({
          goods_id: Number(p.goodsId) || p.goodsId,
          spec_id: Number(p.specId) || p.specId,
          quantity: Number(p.quantity),
        })),
        reason: data.reason || undefined,
        remark: data.description || undefined,
        images: data.images.length > 0 ? data.images : undefined,
      });
      const ok = res.error === false || Number(res.error) === 0;
      if (!ok) {
        showToast(res.message || res.msg || '售后申请失败', 'error');
        return;
      }
      const names = validProducts.map((p) => p.productName).join('、');
      showToast(res.message || `售后申请已提交：${names}`);
      setModal(null);
      void fetchDetail();
    } catch {
      showToast('售后申请失败，请稍后重试', 'error');
    } finally {
      setAfterSaleSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500 mb-3" />
        <p className="text-sm text-foreground-500">正在加载订单...</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <div className="w-16 h-16 bg-background-200 rounded-full flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-2xl text-foreground-400" />
        </div>
        <h2 className="text-lg font-semibold text-foreground-900 mb-2">订单未找到</h2>
        {loadError && <p className="text-sm text-foreground-500 mb-3">{loadError}</p>}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void fetchDetail()}
            className="px-5 py-2 border border-background-300 text-foreground-700 rounded-lg text-sm hover:bg-background-100 transition-colors"
          >
            重新加载
          </button>
          <button
            type="button"
            onClick={() => {
              if (typeof window !== 'undefined' && window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/orders');
              }
            }}
            className="px-5 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm transition-colors"
          >
            返回订单列表
          </button>
        </div>
      </div>
    );
  }

  const stateTag = getOrderStateTag(order.stateLabel, order.isBackorder);
  const isActuallyEditable = editMode;
  const itemsToDisplay = isActuallyEditable ? editableItems : order.items;
  const quickActions = getQuickActions(order);

  const headerActions = (
    <>
      {editMode && (
        <>
          <button
            type="button"
            onClick={() => setShowProductSelector(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-background-100 hover:bg-background-200 text-foreground-700 border border-background-300 transition-colors whitespace-nowrap"
          >
            <i className="ri-add-line" />
            添加物资
          </button>
          <button
            type="button"
            onClick={() => void handleSaveEdit()}
            disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md bg-accent-500 hover:bg-accent-600 text-background-50 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <i className={`ri-save-line ${saving ? 'hidden' : ''}`} />
            {saving && <i className="ri-loader-4-line animate-spin" />}
            {saving ? '保存中...' : '保存修改'}
          </button>
          <button
            type="button"
            onClick={handleCancelEdit}
            disabled={saving}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            取消
          </button>
        </>
      )}

      {!editMode &&
        quickActions.map((a) => (
          <button
            key={a.key}
            type="button"
            onClick={() => handleAction(a.key)}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border transition-colors cursor-pointer whitespace-nowrap ${
              a.primary
                ? 'border-primary-500 bg-primary-500 text-background-50 hover:bg-primary-600 hover:border-primary-600'
                : a.secondary
                  ? 'border-secondary-500 bg-secondary-500 text-background-50 hover:bg-secondary-600 hover:border-secondary-600'
                  : a.danger
                    ? 'border-primary-200 text-primary-600 hover:bg-primary-50 hover:border-primary-300'
                    : 'border-background-200 text-foreground-600 hover:bg-background-50 hover:border-primary-300 hover:text-primary-600'
            }`}
          >
            <i className={`${a.icon} text-xs`} />
            {a.label}
          </button>
        ))}
    </>
  );

  return (
    <div className="flex-1 bg-background-100">
      {toast && (
        <div
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-[70] px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2 transition-all ${
            toast.type === 'success'
              ? 'bg-accent-500 text-background-50'
              : 'bg-red-500 text-white'
          }`}
        >
          <i
            className={
              toast.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
            }
          />
          {toast.message}
        </div>
      )}

      <div className="px-4 pb-4 pt-0 md:px-6 md:pb-6">
        {editMode && (
          <div className="flex items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-accent-100 text-accent-800">
              <i className="ri-edit-line" />
              编辑中
            </span>
          </div>
        )}

        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-background-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground-900">采购信息</h3>
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                style={{ backgroundColor: stateTag.color, color: '#fff' }}
              >
                {order.displayState || stateTag.label}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              {editMode ? (
                <OrderMetaEditFields
                  value={editableMeta}
                  onChange={setEditableMeta}
                  disabled={saving}
                  layout="grid"
                />
              ) : (
                <>
                  <div>
                    <span className="text-foreground-500 text-xs">使用科室</span>
                    <p className="text-foreground-800 mt-0.5 font-medium">{order.department || '-'}</p>
                  </div>
                  <div>
                    <span className="text-foreground-500 text-xs">采购人</span>
                    <p className="text-foreground-800 mt-0.5 font-medium">{order.orderPerson || '-'}</p>
                  </div>
                  <div>
                    <span className="text-foreground-500 text-xs">创建日期</span>
                    <p className="text-foreground-800 mt-0.5">
                      {order.createdAt ? formatOrderDate(order.createdAt) : '-'}
                    </p>
                  </div>
                </>
              )}
              {order.approvedAt && (
                <div>
                  <span className="text-foreground-500 text-xs">审批完成日期</span>
                  <p className="text-foreground-800 mt-0.5">{formatOrderDate(order.approvedAt)}</p>
                </div>
              )}
              {order.printedAt && (
                <div>
                  <span className="text-foreground-500 text-xs">单据打印日期</span>
                  <p className="text-foreground-800 mt-0.5">{formatOrderDate(order.printedAt)}</p>
                </div>
              )}
              {order.shippedAt && (
                <div>
                  <span className="text-foreground-500 text-xs">配送日期</span>
                  <p className="text-foreground-800 mt-0.5">{formatOrderDate(order.shippedAt)}</p>
                </div>
              )}
              {order.receivedAt && (
                <div>
                  <span className="text-foreground-500 text-xs">签收日期</span>
                  <p className="text-foreground-800 mt-0.5">{formatOrderDate(order.receivedAt)}</p>
                </div>
              )}
              {order.completedAt && (
                <div>
                  <span className="text-foreground-500 text-xs">完成日期</span>
                  <p className="text-foreground-800 mt-0.5">{formatOrderDate(order.completedAt)}</p>
                </div>
              )}
              {order.cancelledAt && (
                <div>
                  <span className="text-foreground-500 text-xs">取消日期</span>
                  <p className="text-foreground-800 mt-0.5">{formatOrderDate(order.cancelledAt)}</p>
                </div>
              )}
            </div>
          </div>

          {order.status === 'rejected' && (
            <div className="bg-amber-50 rounded-xl border border-amber-200 p-5">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-close-circle-line text-amber-600 text-lg" />
                <h3 className="text-sm font-semibold text-amber-800">采购单已被驳回</h3>
              </div>
              <p className="text-sm text-amber-700 leading-relaxed">
                {order.rejectReason || '审批人驳回了此采购申请'}
              </p>
              {order.rejectedAt && (
                <p className="text-xs text-amber-600 mt-2">
                  驳回时间：{new Date(order.rejectedAt).toLocaleString('zh-CN')}
                </p>
              )}
            </div>
          )}

          <OrderItemsTable
            items={itemsToDisplay}
            editMode={editMode}
            showPlanUsage={order.isPurchasePlanEnabled}
            showOutsideColumn
            onUpdateQuantity={handleUpdateQuantity}
            onUpdateSpec={handleUpdateSpec}
            onUpdateProduct={handleUpdateProduct}
            onUpdateRemark={handleUpdateRemark}
            onDeleteItem={handleDeleteItem}
            onAddItem={() => setShowProductSelector(true)}
            lastItemError={lastItemError}
            headerActions={headerActions}
            footerExtra={
              editMode ? (
                <div>
                  <label className="text-sm font-medium text-foreground-800 mb-2 block">
                    订单备注
                    <span className="ml-1 text-accent-600 font-normal text-xs">可修改</span>
                  </label>
                  <textarea
                    value={editableRemark}
                    disabled={saving}
                    onChange={(e) => setEditableRemark(e.target.value.slice(0, 255))}
                    rows={3}
                    maxLength={255}
                    placeholder="可填写订单备注（选填）"
                    className="w-full px-3 py-2.5 rounded-lg border border-background-300 text-sm outline-none resize-none transition-colors focus:border-primary-400 disabled:bg-background-100"
                  />
                  <p className="text-xs text-foreground-400 mt-1.5 text-right">
                    {editableRemark.length}/255
                  </p>
                </div>
              ) : (
                <div className="text-sm">
                  <span className="text-foreground-500 text-xs">订单备注</span>
                  <p className="text-foreground-800 mt-0.5">{order.remark || '无'}</p>
                </div>
              )
            }
          />

          <OrderProgressPanel
            status={order.status}
            stateLabel={order.stateLabel}
            isBackorder={order.isBackorder}
            orderPerson={order.orderPerson || '提交人'}
            createdAt={order.createdAt}
            approvedAt={order.approvedAt}
            printedAt={order.printedAt}
            shippedAt={order.shippedAt}
            receivedAt={order.receivedAt}
            completedAt={order.completedAt}
            approvalSteps={order.approvalSteps}
            signImageCount={order.signImages.length}
            onOpenSignVouchers={
              order.signImages.length > 0 ? () => setModal({ type: 'sign-voucher' }) : undefined
            }
          />
        </div>
      </div>

      <ApproveModal
        isOpen={modal?.type === 'approve'}
        orderSn={order.orderSn}
        department={order.department}
        orderPerson={order.orderPerson}
        createdAt={order.createdAt}
        items={order.items.map((item) => ({
          name: item.name,
          spec: item.spec,
          quantity: item.quantity,
          unit: item.unit,
        }))}
        submitting={approveSubmitting}
        onCancel={() => {
          if (!approveSubmitting) setModal(null);
        }}
        onApprove={(remark) => void handleApproveSubmit(true, remark)}
        onReject={(remark) => void handleApproveSubmit(false, remark)}
      />

      <ConfirmModal
        isOpen={modal?.type === 'confirm-reverse'}
        title="反审确认"
        message={`确认对订单 ${order.orderSn} 执行反审？反审后订单将回到审批中状态，可重新审批。`}
        confirmLabel="确认反审"
        danger
        loading={reverseSubmitting}
        onCancel={() => {
          if (!reverseSubmitting) setModal(null);
        }}
        onConfirm={() => void handleReverseConfirm()}
      />

      <CancelOrderModal
        isOpen={modal?.type === 'confirm-cancel'}
        orderId={order.orderSn}
        submitting={cancelSubmitting}
        onCancel={() => {
          if (!cancelSubmitting) setModal(null);
        }}
        onConfirm={handleCancelConfirm}
      />

      <ConfirmModal
        isOpen={modal?.type === 'confirm-rebuy'}
        title="再次购买"
        message={`确认将订单 ${order.orderSn} 中的所有物资重新加入购物车？确认后将自动跳转到购物车进行结算。`}
        confirmLabel="确认加入购物车"
        onCancel={() => setModal(null)}
        onConfirm={handleRebuyConfirm}
      />

      <SignReceiptModal
        isOpen={modal?.type === 'sign'}
        orderId={order.orderSn}
        mode={modal?.type === 'sign' ? modal.mode : 'sign'}
        submitting={signSubmitting}
        onCancel={() => {
          if (!signSubmitting) setModal(null);
        }}
        onConfirm={handleSignConfirm}
      />

      <AfterSaleModal
        isOpen={modal?.type === 'aftersale'}
        orderId={order.orderSn}
        items={order.items.map((item) => ({
          productId: item.productId,
          goodsId: item.goodsId || item.productId,
          specId: item.specId || '',
          name: item.name,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          unit: item.unit,
          spec: item.spec,
        }))}
        submitting={afterSaleSubmitting}
        onCancel={() => {
          if (!afterSaleSubmitting) setModal(null);
        }}
        onSubmit={handleAfterSaleSubmit}
      />

      <SignVoucherModal
        isOpen={modal?.type === 'sign-voucher'}
        orderId={order.orderSn}
        images={order.signImages}
        onClose={() => setModal(null)}
      />

      <ProductSelector
        isOpen={showProductSelector}
        onClose={() => setShowProductSelector(false)}
        onSelect={handleAddProduct}
        existingProductIds={[]}
      />
    </div>
  );
}
